import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { fromJS } from 'immutable';
import fileDownload from 'js-file-download';
import React, { useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import { ApiContext, EntitiesContext } from '../../../App';
import { useGetAccounts } from '../../../hooks/useAccount';
import { useCreateToken } from '../../../hooks/useAdminAuth';
import ContactModal from './ContactModal';
import { useNavigate } from 'react-router-dom';

export default function ConsignorList({ consignorSelected, data, filter, setFilter, isFiltering }) {
  const api = useContext(ApiContext);
  const entities = useContext(EntitiesContext);
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [list, setList] = useState([]);
  const accounts = useGetAccounts('consigner');
  const auth = useCreateToken();
  const icon = <FontAwesomeIcon icon={faAngleDown} />;
  const [selectedConsignors, setSelectedConsignors] = useState([]); // Hold multiple selected consignors
  const [consignorDetails, setConsignorDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [isLookupModalOpen, setIsLookupModalOpen] = useState(false);

  const metaLists = entities.state.get('list');

  useEffect(() => {
    if (!accounts.isLoading && (data.count() > 0 || showAll)) {
      setList(entities.state
        .get('account')
        .filter(a => a.role === 'consigner')
        .sortBy(a => a.id)
        .reverse()
        .valueSeq()
        .filter(c => {
          if (showAll) return true;
          return data.filter(d => d.account_id === c.id).count() > 0;
        })
        .map(c => ({
          value: c.id,
          label: `${c.first_name} ${c.last_name} (${data.filter(i => i.account_id === c.id).count()})`
        })).toJS());
    }
  }, [accounts.isLoading, data, showAll]);

  const onChange = (value, type) => {
    const imFilter = fromJS(filter);
    if (type.action === 'remove-value') {
      setFilter(imFilter.delete(imFilter.findIndex(f => f.get('value') === type.removedValue?.value)).toJS());
      setSelectedConsignors(value); // Update with the remaining selected consignors
    } else {
      setFilter(value);
      setSelectedConsignors(value); // Store all selected consignors
    }
  };

  const fetchConsignorDetails = () => {
    const detailsList = selectedConsignors.map((consignor) => {
      const consignorData = entities.state.get('account').find(account => account.id === consignor.value);
      if (consignorData) {
        return {
          id: consignorData.id,
          name: `${consignorData.first_name} ${consignorData.last_name}`,
          email: consignorData.email,
          phone: consignorData.phone,
          address: consignorData.address,
        };
      }
      return null;
    }).filter(Boolean); // Filter out any null values

    setConsignorDetails(detailsList);
    setIsModalOpen(true);
  };

  const handleFetchDetails = () => {
    if (selectedConsignors.length > 0) { // Check if there's at least one selected consignor
      fetchConsignorDetails(); // Fetch details for all selected consignors
    }
  };

  const handleLoginAsConsignor = () => {
    if (selectedConsignors.length === 1) {
      const consignorData = entities.state.get('account').find(account => account.id === selectedConsignors[0].value);
      if (consignorData) {
        // Store the current admin's auth before switching
        const currentAuth = window.localStorage.getItem('auth');
        if (currentAuth) {
          window.localStorage.setItem('originalAdminAuth', currentAuth);
        }

        auth
          .mutateAsync(consignorData)
          .then((res) => {
            // Set flag to indicate this is a "login as" session
            window.localStorage.setItem('loginAsSession', 'true');
            navigate(`/admin/auth/${res.data}`);
          })
          .catch((err) => console.log(err));
      }
    }
  };

  const handleLookupById = () => {
    const id = Number(lookupId);
    if (!Number.isInteger(id) || id <= 0) return;
    const account = entities.state.get('account').get(id);
    setLookupResult(account?.role === 'consigner' ? {
      id: account.id,
      name: account.fullName,
      email: account.email,
      phone: account.phone,
      address: account.address,
    } : null);
    setIsLookupModalOpen(true);
  };

  const filterOption = (option, inputValue) => {
    // if (inputValue !== '') {
    //   const matches = data.filter(d => d.name.toLowerCase().includes(inputValue.toLowerCase()));
    //   if (matches.filter(m => m.account_id === option.value).count() > 0 || option.label.toLowerCase().includes(inputValue.toLowerCase())) {
    //     return option;
    //   }
    // }
    
    if (inputValue !== '') {
      if (option.label.toLowerCase().includes(inputValue.toLowerCase()) || parseInt(option.value) === parseInt(inputValue)) {
        return option;
      }
    } else {
      return option; // Return the option if there's no input or if it matches
    }
  };

  const exportData = () => {
    api.get('/admin/csv')
      .then(res => fileDownload(res.data, `export-${Date.now()}.csv`));
  };

  const csvField = (value) => {
    if (value == null) return '';
    let str = String(value);
    // Prevent formula injection when opened in Excel/Sheets
    if (['=', '+', '-', '@'].includes(str[0])) str = '\t' + str;
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r') || str.includes('\t')) {
      return '"' + str.replaceAll('"', '""') + '"';
    }
    return str;
  };

  const csv = () => {
    const header = 'id,account_id,name,price,length,discounted,condition,category,clothing_type,size,boot_size,event_type,kids size,boot type nordic,ski type nordic,accessories\n';

    const rows = data
      .filter(i => isFiltering() ? consignorSelected(i) : true)
      .map(p => [
        p.id,
        p.account_id,
        csvField(p.name),
        p.price,
        p['length'],
        p.discounted,
        csvField(metaLists.get(p.condition)?.label),
        csvField(metaLists.get(p.category)?.label),
        csvField(metaLists.get(p['clothing_type'])?.label),
        csvField(metaLists.get(p.size)?.label),
        csvField(metaLists.get(p['boot_size'])?.label),
        csvField(metaLists.get(p['event_type'])?.label),
        csvField(metaLists.get(p['kids_size'])?.label),
        csvField(metaLists.get(p['boot_type_nordic'])?.label),
        csvField(metaLists.get(p['ski_type_nordic'])?.label),
        csvField(metaLists.get(p.accessories)?.label)
      ].join(','));

    const file = header + rows.join('\n');
    fileDownload(file, 'export.csv');
  };


  return (
    <div className="columns">
      <div className="column">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <input
            className="input"
            type="number"
            placeholder="Lookup consignor by ID..."
            value={lookupId}
            onChange={e => setLookupId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLookupById()}
            style={{ maxWidth: '260px', marginRight: '10px' }}
          />
          <button
            className="button"
            onClick={handleLookupById}
            disabled={!lookupId || accounts.isLoading}
          >
            {accounts.isLoading ? 'Loading...' : 'Lookup'}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Select
            isMulti
            onChange={onChange}
            className="basic-multi-select"
            options={list}
            filterOption={filterOption}
            styles={{
              control: (provided) => ({
                ...provided,
                minWidth: '500px', // Set the minimum width to make it longer
                marginRight: '20px', // Increased margin for spacing
              }),
            }}
          />

          <div style={{ marginRight: '20px' }}>
            <input
              type="checkbox"
              name="show-all"
              checked={showAll}
              onChange={() => setShowAll(!showAll)}
            />
            <span style={{ marginLeft: "5px" }}>Show All</span>
          </div>

          <div className={classNames('dropdown', { 'is-active': showExportOptions })} style={{ position: 'relative', marginRight: '20px' }}>
            <div className="dropdown-trigger">
              <button
                className="button"
                onClick={() => setShowExportOptions(!showExportOptions)}
                aria-haspopup="true"
                aria-controls="dropdown-menu"
              >
                <span>Export</span>
                <span className="icon is-small">
                  {icon}
                </span>
              </button>
            </div>
            <div className="dropdown-menu" id="dropdown-menu" role="menu" onMouseLeave={() => setShowExportOptions(false)}>
              <div className="dropdown-content">
                <a onClick={csv} className="dropdown-item">Labels</a>
                <a onClick={exportData} className="dropdown-item">Consignors</a>
              </div>
            </div>
          </div>

          <button
            className='button is-link'
            onClick={handleFetchDetails}
            disabled={selectedConsignors.length === 0} // Enable button only if consignors are selected
          >
            Fetch Consignor Details
          </button>

          <button
            className='button is-primary'
            onClick={handleLoginAsConsignor}
            disabled={selectedConsignors.length !== 1 || auth.isLoading}
            style={{ marginLeft: '10px' }}
          >
            Login as Consignor
          </button>
        </div>
      </div>

      <ContactModal isOpen={isLookupModalOpen} onClose={() => setIsLookupModalOpen(false)} title={`Lookup: ID ${lookupId}`}>
        {lookupResult ? (
          <div>
            <p><strong>ID:</strong> {lookupResult.id}</p>
            <p><strong>Name:</strong> {lookupResult.name}</p>
            <p><strong>Email:</strong> {lookupResult.email}</p>
            <p><strong>Phone:</strong> {lookupResult.phone}</p>
            <br />
            <div className="columns">
              <a
                href={`tel:${lookupResult.phone}`}
                className="button is-link is-small"
                style={{ marginTop: '10px', marginRight: '10px' }}
              >
                Call {lookupResult.name}
              </a>
              <a
                href={`mailto:${lookupResult.email}`}
                className="button is-link is-small"
                style={{ marginTop: '10px' }}
              >
                Email {lookupResult.name}
              </a>
            </div>
          </div>
        ) : (
          <p>No consignor found with ID <strong>{lookupId}</strong>.</p>
        )}
      </ContactModal>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Consignor Details">
        {consignorDetails && consignorDetails.length > 0 ? (
          consignorDetails.map((details, index) => (
            <div key={details.id} style={{ marginBottom: '20px' }}>
              <p><strong>ID:</strong> {details.id}</p>
              <p><strong>Name:</strong> {details.name}</p>
              <p><strong>Email:</strong> {details.email}</p>
              <p><strong>Phone:</strong> {details.phone}</p>
              <br />
              <div className="columns">
                <a
                  href={`tel:${details.phone}`}
                  className="button is-link is-small"
                  style={{ marginTop: '10px', marginRight: '10px' }}
                >
                  Call {details.name}
                </a>
                <a
                  href={`mailto:${details.email}`}
                  className="button is-link is-small"
                  style={{ marginTop: '10px' }}
                >
                  Email {details.name}
                </a>
              </div>
              {index < consignorDetails.length - 1 && <hr />} {/* Separator between consignors */}
            </div>
          ))
        ) : (
          <p>No details available.</p>
        )}
      </ContactModal>

    </div>
  );
}
